import React, { useRef, useCallback, useMemo, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import BendLayer from './BendLayer';
import { Stage, Container, Graphics, BitmapText, Sprite } from '@pixi/react';
import { Texture } from '@pixi/core';
import { BitmapFont } from '@pixi/text-bitmap';

// ─── Cursor constants ────────────────────────────────────────────────────────
const _rotateSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20"><path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" fill="#222"/></svg>`;
const ROTATE_CURSOR = `url("data:image/svg+xml;base64,${btoa(_rotateSvg)}") 10 10, alias`;

// ─── Shape label font ────────────────────────────────────────────────────────
// A shared bitmap font so shape numbers/blocks batch like sprites instead of
// each being its own canvas-rendered PIXI.Text (which doesn't scale past a
// few hundred labels). Rendered in white and recolored per-shape via `tint`.
const SHAPE_LABEL_FONT = 'ShapeLabelFont';
if (!BitmapFont.available[SHAPE_LABEL_FONT]) {
  BitmapFont.from(
    SHAPE_LABEL_FONT,
    { fontFamily: 'Arial', fontWeight: 'bold', fill: 0xffffff, fontSize: 64 },
    { chars: BitmapFont.ALPHANUMERIC }
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function parseColor(hexStr) {
  return parseInt((hexStr || '003eb3').replace('#', ''), 16);
}

function getContrastingTextColor(hexColor) {
  if (!hexColor) return 0x000000;
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 128 ? 0x000000 : 0xffffff;
}

// Flat [x0,y0, x1,y1, x2,y2, x3,y3] corners of a w×h rect rotated (degrees)
// around its own center — for g.drawPolygon(). `expand` grows the rect
// symmetrically first (for the +2px/+4px selection/hover border insets).
function rotatedRectPoints(x, y, w, h, rotationDeg, expand = 0) {
  const ex = x - expand, ey = y - expand, ew = w + expand * 2, eh = h + expand * 2;
  if (!rotationDeg) {
    return [ex, ey, ex + ew, ey, ex + ew, ey + eh, ex, ey + eh];
  }
  const cx = x + w / 2, cy = y + h / 2;
  const rad = rotationDeg * Math.PI / 180;
  const cos = Math.cos(rad), sin = Math.sin(rad);
  const hw = ew / 2, hh = eh / 2;
  const corners = [[-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]];
  const points = [];
  corners.forEach(([dx, dy]) => {
    points.push(cx + dx * cos - dy * sin, cy + dx * sin + dy * cos);
  });
  return points;
}

// ─── Batched shapes layer ──────────────────────────────────────────────────────
// Draws ALL shapes in a single Graphics batch — zero React nodes per shape.
const BatchedShapesLayer = React.memo(({
  shapes,
  selectedIds,
  firstMarkers,
  selectedMarker,
  guestList,
  viewOnly,
  dragOffset,
  hoveredId,
}) => {
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  // Build a guest map once
  const guestMap = useMemo(() => {
    const m = new Map();
    if (guestList) guestList.forEach(c => m.set(c.chair, c));
    return m;
  }, [guestList]);

  const draw = useCallback((g) => {
    g.clear();

    for (let i = 0; i < shapes.length; i++) {
      const shape = shapes[i];
      const isSelected = selectedSet.has(shape.id);
      const isFirst = firstMarkers.has(shape.id);
      const isHovered = hoveredId === shape.id;

      let x = shape.x;
      let y = shape.y;
      if (isSelected && dragOffset) {
        x += dragOffset.x;
        y += dragOffset.y;
      }
      const rotation = shape.rotation || 0;

      // Fill color
      let color;
      if (isFirst && !viewOnly) color = 0xff8c00;
      else if (shape.special) color = 0x000000;
      else if (selectedMarker === shape.id) color = 0xff8c00;
      else color = parseColor(shape.fill);

      g.beginFill(color, 0.8);
      g.drawPolygon(rotatedRectPoints(x, y, shape.width, shape.height, rotation));
      g.endFill();

      // Special border
      if (shape.special) {
        g.lineStyle(3, 0xffd700, 0.7);
        g.drawPolygon(rotatedRectPoints(x, y, shape.width, shape.height, rotation, 3));
        g.lineStyle(0);
      }

      // View-only matched border
      if (viewOnly) {
        const chair = guestMap.get(shape.id);
        const borderColor = chair?.matching?.matched ? 0x00ff00 : 0xff0000;
        g.lineStyle(2, borderColor, 0.6);
        g.drawPolygon(rotatedRectPoints(x, y, shape.width, shape.height, rotation, 1));
        g.lineStyle(0);
      }

      // Selection border
      if (isSelected) {
        g.lineStyle(2, 0xff0000, 0.9);
        g.drawPolygon(rotatedRectPoints(x, y, shape.width, shape.height, rotation, 2));
        g.lineStyle(0);
      }

      // Hover border
      if (isHovered && !isSelected) {
        g.lineStyle(1, 0xffffff, 0.5);
        g.drawPolygon(rotatedRectPoints(x, y, shape.width, shape.height, rotation, 1));
        g.lineStyle(0);
      }
    }
  }, [shapes, selectedSet, firstMarkers, selectedMarker, guestMap, viewOnly, dragOffset, hoveredId]);

  return <Graphics draw={draw} />;
});
BatchedShapesLayer.displayName = 'BatchedShapesLayer';

// ─── Text labels layer ─────────────────────────────────────────────────────────
// Only rendered when there are shapes with number/block text.
// Uses BitmapText (batches like sprites) instead of per-instance canvas Text,
// which stops scaling once there are more than a few hundred labeled shapes.
const ShapeLabels = React.memo(({ shapes, selectedIds, dragOffset }) => {
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const labeled = useMemo(() => shapes.filter(s => s.number || s.block), [shapes]);

  if (labeled.length === 0) return null;

  return (
    <Container>
      {labeled.map(shape => {
        const isSelected = selectedSet.has(shape.id);
        const ox = shape.x + (isSelected && dragOffset ? dragOffset.x : 0);
        const oy = shape.y + (isSelected && dragOffset ? dragOffset.y : 0);
        const textColor = getContrastingTextColor(shape.fill);
        // Rotate the label container around the shape's own center so the
        // text tilts along with a rotated shape, not just its position.
        return (
          <Container
            key={shape.id}
            x={ox + shape.width / 2}
            y={oy + shape.height / 2}
            rotation={(shape.rotation || 0) * Math.PI / 180}
          >
            {shape.number && (
              <BitmapText
                text={shape.number.toString()}
                x={shape.width / 4}
                y={shape.height / 4}
                anchor={0.5}
                tint={textColor}
                style={{ fontName: SHAPE_LABEL_FONT, fontSize: Math.max(8, shape.width / 2) }}
              />
            )}
            {shape.block && (
              <BitmapText
                text={shape.block.toString()}
                x={-shape.width / 4}
                y={-shape.height / 4}
                anchor={0.5}
                tint={textColor}
                style={{ fontName: SHAPE_LABEL_FONT, fontSize: Math.max(8, shape.width / 2) }}
              />
            )}
          </Container>
        );
      })}
    </Container>
  );
});
ShapeLabels.displayName = 'ShapeLabels';

// ─── Background image component ────────────────────────────────────────────────
const BackgroundImageComponent = ({ backgroundImage, bgTransform }) => {
  const [texture, setTexture] = useState(null);

  useEffect(() => {
    if (!backgroundImage) { setTexture(null); return; }
    const t = Texture.from(backgroundImage.src || backgroundImage);
    if (t.valid) {
      setTexture(t);
    } else {
      const onLoaded = () => setTexture(t);
      t.baseTexture.on('loaded', onLoaded);
      return () => t.baseTexture.off('loaded', onLoaded);
    }
  }, [backgroundImage]);

  if (!texture || !bgTransform) return null;
  const rot = bgTransform.rot ?? 0;
  return (
    <Sprite
      texture={texture}
      x={bgTransform.x + bgTransform.w / 2}
      y={bgTransform.y + bgTransform.h / 2}
      width={bgTransform.w}
      height={bgTransform.h}
      anchor={0.5}
      rotation={rot}
    />
  );
};

// ─── Background image overlay (selection border + resize corners) ─────────────
const BgImageOverlay = React.memo(({ bgTransform, bgSelected }) => {
  const w = bgTransform?.w ?? 0;
  const h = bgTransform?.h ?? 0;
  const rot = bgTransform?.rot ?? 0;
  const ROT_OFFSET = 32; // px above top edge in un-rotated image space
  const ROT_R = 10;      // radius of the rotation handle circle
  const drawBorder = useCallback((g) => {
    g.clear();
    g.lineStyle(2, 0x0066ff, 0.9);
    g.drawRect(0, 0, w, h);
    g.lineStyle(1, 0x0066ff, 0.4);
    g.moveTo(w / 2 - 8, h / 2); g.lineTo(w / 2 + 8, h / 2);
    g.moveTo(w / 2, h / 2 - 8); g.lineTo(w / 2, h / 2 + 8);
    // Line from top-center to rotation handle
    g.lineStyle(1.5, 0x0066ff, 0.7);
    g.moveTo(w / 2, 0);
    g.lineTo(w / 2, -ROT_OFFSET);
  }, [w, h]);

  const drawRotHandle = useCallback((g) => {
    g.clear();
    g.beginFill(0xe8f4ff, 1);
    g.lineStyle(2.5, 0x0044cc, 1);
    g.drawCircle(0, 0, ROT_R);
    g.endFill();
  }, [ROT_R]);

  if (!bgSelected || !bgTransform) return null;
  const { x, y } = bgTransform;
  const shortest = Math.min(w, h);
  const HS = Math.round(Math.min(Math.max(8, shortest * 0.04), 24));   // corner square
  const ES = Math.round(Math.min(Math.max(14, shortest * 0.07), 48));  // edge long side
  const ET = Math.round(Math.min(Math.max(5,  shortest * 0.02), 12));  // edge short side
  const corners = [
    { id: 'nw', cx: -HS / 2,          cy: -HS / 2,          cursor: 'grab', isCorner: true },
    { id: 'ne', cx: w - HS / 2,       cy: -HS / 2,          cursor: 'grab', isCorner: true },
    { id: 'se', cx: w - HS / 2,       cy: h - HS / 2,       cursor: 'grab', isCorner: true },
    { id: 'sw', cx: -HS / 2,          cy: h - HS / 2,       cursor: 'grab', isCorner: true },
    { id: 'n',  cx: w / 2 - ES / 2,   cy: -ET / 2,          cursor: 'grab', isCorner: false },
    { id: 's',  cx: w / 2 - ES / 2,   cy: h - ET / 2,       cursor: 'grab', isCorner: false },
    { id: 'e',  cx: w - ET / 2,       cy: h / 2 - ES / 2,   cursor: 'grab', isCorner: false },
    { id: 'w',  cx: -ET / 2,          cy: h / 2 - ES / 2,   cursor: 'grab', isCorner: false },
  ];
  // The Container is positioned at (x, y) (top-left) and rotated around the image center
  const cx = x + w / 2;
  const cy = y + h / 2;
  return (
    <Container x={cx} y={cy} rotation={rot}>
      {/* offset child back to top-left so resize handles use image-local coords */}
      <Container x={-w / 2} y={-h / 2}>
        <Graphics draw={drawBorder} />
        {corners.map(({ id, cx: hx, cy: hy, cursor, isCorner }) => (
          <Container key={id} x={hx} y={hy}>
            <Graphics
              cursor={cursor}
              draw={(g) => {
                g.clear();
                g.beginFill(0xffffff, 1);
                g.lineStyle(2, 0x0066ff, 1);
                if (isCorner) {
                  g.drawRect(0, 0, HS, HS);
                } else {
                  const isHoriz = id === 'n' || id === 's';
                  g.drawRect(0, 0, isHoriz ? ES : ET, isHoriz ? ET : ES);
                }
                g.endFill();
              }}
            />
          </Container>
        ))}
        {/* Rotation handle — circle above top-center */}
        <Container x={w / 2} y={-ROT_OFFSET} cursor="grab">
          <Graphics draw={drawRotHandle} />
        </Container>
      </Container>
    </Container>
  );
});
BgImageOverlay.displayName = 'BgImageOverlay';

// ─── Scan path lines ───────────────────────────────────────────────────────────
const ScanPathLines = ({ scanPaths, shapes, currentScanPathId }) => {
  const shapeMap = useMemo(() => {
    const m = new Map();
    shapes.forEach(s => m.set(s.id, s));
    return m;
  }, [shapes]);

  const draw = useCallback((g) => {
    g.clear();
    scanPaths.forEach((scanPath) => {
      if (scanPath.path.length <= 1) return;
      const color = currentScanPathId === scanPath.id ? 0xff8c00 : 0xff0000;
      g.lineStyle(4, color, 0.8);
      for (let i = 0; i < scanPath.path.length - 1; i++) {
        const s1 = shapeMap.get(scanPath.path[i]);
        const s2 = shapeMap.get(scanPath.path[i + 1]);
        if (s1 && s2) {
          g.moveTo(s1.x + s1.width / 2, s1.y + s1.height / 2);
          g.lineTo(s2.x + s2.width / 2, s2.y + s2.height / 2);
        }
      }
    });
  }, [scanPaths, shapeMap, currentScanPathId]);

  return <Graphics draw={draw} />;
};

// ─── Selection rectangle ───────────────────────────────────────────────────────
const SelectionRectangle = ({ selectionRect, visible }) => {
  const draw = useCallback((g) => {
    g.clear();
    if (visible && selectionRect) {
      g.beginFill(0x0099ff, 0.15);
      g.drawRect(selectionRect.x, selectionRect.y, selectionRect.width, selectionRect.height);
      g.endFill();
      g.lineStyle(1, 0x0099ff, 0.8);
      g.drawRect(selectionRect.x, selectionRect.y, selectionRect.width, selectionRect.height);
    }
  }, [selectionRect, visible]);
  return <Graphics draw={draw} />;
};

// ─── Bounding Box with resize anchors ─────────────────────────────────────────
// Standard directional resize cursors (nw/se and ne/sw share a diagonal, n/s and e/w share an axis)
const RESIZE_ANCHOR_CURSORS = {
  nw: 'nwse-resize', se: 'nwse-resize',
  ne: 'nesw-resize', sw: 'nesw-resize',
  n: 'ns-resize', s: 'ns-resize',
  e: 'ew-resize', w: 'ew-resize',
};

// Shared between the BoundingBox's rotate-handle rendering and its hit-test in
// handleMouseDown, so the clickable area always matches what's drawn.
const SHAPE_ROT_OFFSET = 48;
const SHAPE_ROT_HIT = 12;
const SHAPE_ROT_R = 7;
// While dragging the rotate handle, snap to 0/90/180/270° once within this
// many degrees of one of them.
const SHAPE_ROT_SNAP_THRESHOLD = 2;

const BoundingBox = React.memo(({ bounds, onResizeStart, onResizeDrag, isResizing, resizeAnchor, resizeOffset }) => {
  const anchorSize = 12;
  const anchors = [
    { id: 'nw', x: -anchorSize/2,                   y: -anchorSize/2                   },
    { id: 'n',  x: bounds.width/2 - anchorSize/2,   y: -anchorSize/2                   },
    { id: 'ne', x: bounds.width - anchorSize/2,      y: -anchorSize/2                   },
    { id: 'e',  x: bounds.width - anchorSize/2,      y: bounds.height/2 - anchorSize/2  },
    { id: 'se', x: bounds.width - anchorSize/2,      y: bounds.height - anchorSize/2    },
    { id: 's',  x: bounds.width/2 - anchorSize/2,   y: bounds.height - anchorSize/2    },
    { id: 'sw', x: -anchorSize/2,                   y: bounds.height - anchorSize/2    },
    { id: 'w',  x: -anchorSize/2,                   y: bounds.height/2 - anchorSize/2  },
  ];

  return (
    <Container eventMode="static">
      <Graphics draw={(g) => {
        g.clear();
        g.lineStyle(2, 0x0066ff, 0.8);
        g.drawRect(bounds.x, bounds.y, bounds.width, bounds.height);
        // Line from top-center to the rotate handle
        g.lineStyle(1.5, 0x0066ff, 0.7);
        g.moveTo(bounds.x + bounds.width / 2, bounds.y);
        g.lineTo(bounds.x + bounds.width / 2, bounds.y - SHAPE_ROT_OFFSET);
      }} />
      <Container
        x={bounds.x + bounds.width / 2}
        y={bounds.y - SHAPE_ROT_OFFSET}
        cursor={ROTATE_CURSOR}
      >
        <Graphics draw={(g) => {
          g.clear();
          g.beginFill(0xe8f4ff, 1);
          g.lineStyle(2.5, 0x0044cc, 1);
          g.drawCircle(0, 0, SHAPE_ROT_R);
          g.endFill();
        }} />
      </Container>
      {anchors.map(anchor => (
        <Container
          key={anchor.id}
          x={bounds.x + anchor.x}
          y={bounds.y + anchor.y}
          cursor={RESIZE_ANCHOR_CURSORS[anchor.id]}
        >
          <Graphics
            draw={(g) => {
              g.clear();
              g.beginFill(0x0066ff, 1);
              g.drawRect(0, 0, anchorSize, anchorSize);
              g.endFill();
              if (isResizing && resizeAnchor === anchor.id) {
                g.lineStyle(2, 0xffffff, 1);
                g.drawRect(-1, -1, anchorSize + 2, anchorSize + 2);
              }
            }}
          />
        </Container>
      ))}
    </Container>
  );
});
BoundingBox.displayName = 'BoundingBox';

// Main Simple Pixi Editor Component
const PixiEditorSimple = forwardRef(({ 
  shapes, 
  scanPaths, 
  backgroundImage,
  bgImageInitialTransform,
  width, 
  height, 
  selectedIds, 
  firstMarkers,
  selectedMarker,
  guestList,
  onSelect,
  onContextMenu,
  isLocked,
  viewOnly,
  currentScanPathId,
  selectionRect,
  showSelectionRect,
  onMultiSelect,
  onUpdateShapePositions,
  onCommitShapes,
  onBgTransformCommit,
  bendMode = false,
  onApplyBend,
  debugMode = false,
  bgLocked = false,
  ...props
}, ref) => {
  const stageRef = useRef();
  const editorRef = useRef();
  const shapesRef = useRef(shapes);
  useEffect(() => { shapesRef.current = shapes; }, [shapes]);
  const onContextMenuRef = useRef(onContextMenu);
  useEffect(() => { onContextMenuRef.current = onContextMenu; }, [onContextMenu]);
  const onSelectRef = useRef(onSelect);
  useEffect(() => { onSelectRef.current = onSelect; }, [onSelect]);
  const selectedIdsRef = useRef(selectedIds);
  useEffect(() => { selectedIdsRef.current = selectedIds; }, [selectedIds]);
  const viewOnlyRef = useRef(viewOnly);
  useEffect(() => { viewOnlyRef.current = viewOnly; }, [viewOnly]);
  const backgroundImageRef = useRef(backgroundImage);
  useEffect(() => { backgroundImageRef.current = backgroundImage; }, [backgroundImage]);
  const scanPathsRef = useRef(scanPaths);
  useEffect(() => { scanPathsRef.current = scanPaths; }, [scanPaths]);
  const firstMarkersRef = useRef(firstMarkers);
  useEffect(() => { firstMarkersRef.current = firstMarkers; }, [firstMarkers]);

  useImperativeHandle(ref, () => ({
    getBgTransform: () => bgTransformRef.current,
    setBgTransformExternal: (t) => {
      setBgTransform(t);
      bgTransformRef.current = t;
    },
    isBgSelected: () => bgSelectedRef.current,
    fitView: () => fitToContent(shapesRef.current, width, height),
    toBlob: () => {
      const allShapes = shapesRef.current;
      const bgImg = backgroundImageRef.current;
      const bgT = bgTransformRef.current;

      if ((!allShapes || allShapes.length === 0) && !bgImg) {
        return Promise.reject(new Error('Nothing to render'));
      }

      // Compute bounding box — include both shapes and (rotated) bg image
      const PADDING = 20;
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

      if (allShapes) {
        for (const s of allShapes) {
          if (s.x < minX) minX = s.x;
          if (s.y < minY) minY = s.y;
          if (s.x + s.width > maxX) maxX = s.x + s.width;
          if (s.y + s.height > maxY) maxY = s.y + s.height;
        }
      }

      if (bgImg && bgT) {
        const rot = bgT.rot ?? 0;
        const cos = Math.abs(Math.cos(rot));
        const sin = Math.abs(Math.sin(rot));
        const cx = bgT.x + bgT.w / 2;
        const cy = bgT.y + bgT.h / 2;
        const halfW = (bgT.w * cos + bgT.h * sin) / 2;
        const halfH = (bgT.w * sin + bgT.h * cos) / 2;
        minX = Math.min(minX, cx - halfW);
        minY = Math.min(minY, cy - halfH);
        maxX = Math.max(maxX, cx + halfW);
        maxY = Math.max(maxY, cy + halfH);
      }

      if (!isFinite(minX)) return Promise.reject(new Error('Empty bounds'));

      minX -= PADDING; minY -= PADDING; maxX += PADDING; maxY += PADDING;
      const contentW = maxX - minX;
      const contentH = maxY - minY;

      // Render to an offscreen 2D canvas
      const THUMB_MAX = 1200;
      const scale = Math.min(1, THUMB_MAX / Math.max(contentW, contentH));
      const canvasW = Math.round(contentW * scale);
      const canvasH = Math.round(contentH * scale);

      const offscreen = document.createElement('canvas');
      offscreen.width = canvasW;
      offscreen.height = canvasH;
      const ctx = offscreen.getContext('2d');

      // Background fill
      ctx.fillStyle = '#f0f0f0';
      ctx.fillRect(0, 0, canvasW, canvasH);

      // Draw background image (with rotation)
      if (bgImg && bgT) {
        const rot = bgT.rot ?? 0;
        const cx = (bgT.x + bgT.w / 2 - minX) * scale;
        const cy = (bgT.y + bgT.h / 2 - minY) * scale;
        const bw = bgT.w * scale;
        const bh = bgT.h * scale;
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(rot);
        ctx.globalAlpha = 0.9;
        ctx.drawImage(bgImg, -bw / 2, -bh / 2, bw, bh);
        ctx.globalAlpha = 1;
        ctx.restore();
      }

      // Draw shapes on top
      const firstMarkerSet = firstMarkersRef.current || new Set();
      for (const s of (allShapes || [])) {
        const x = (s.x - minX) * scale;
        const y = (s.y - minY) * scale;
        const w = s.width * scale;
        const h = s.height * scale;
        const fill = '#' + (s.fill || '003eb3').replace('#', '');
        ctx.globalAlpha = 0.8;
        // Match live editor: first marker → orange, special → black, else fill
        if (firstMarkerSet.has(s.id)) ctx.fillStyle = '#ff8c00';
        else if (s.special) ctx.fillStyle = '#000000';
        else ctx.fillStyle = fill;
        ctx.fillRect(x, y, w, h);
        ctx.globalAlpha = 1;
      }

      // Draw number/block labels on top of shapes (matches ShapeLabels layer)
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      for (const s of (allShapes || [])) {
        if (!s.number && !s.block) continue;
        const x = (s.x - minX) * scale;
        const y = (s.y - minY) * scale;
        const w = s.width * scale;
        const h = s.height * scale;
        const fontPx = Math.max(8, s.width / 2) * scale;
        ctx.font = `bold ${fontPx}px sans-serif`;
        ctx.fillStyle = getContrastingTextColor(s.fill) === 0xffffff ? '#ffffff' : '#000000';
        // number bottom-right, block top-left — off the centered scan-path line
        if (s.number) ctx.fillText(String(s.number), x + w * 3 / 4, y + h * 3 / 4);
        if (s.block) ctx.fillText(String(s.block), x + w / 4, y + h / 4);
      }

      // Draw scan path lines between shape centers (red, like the live editor)
      const scanPathList = scanPathsRef.current || [];
      if (scanPathList.length) {
        const shapeById = new Map((allShapes || []).map(s => [s.id, s]));
        ctx.globalAlpha = 0.8;
        ctx.strokeStyle = '#ff0000';
        ctx.lineWidth = Math.max(1, 4 * scale);
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        for (const scanPath of scanPathList) {
          const path = scanPath?.path;
          if (!path || path.length <= 1) continue;
          ctx.beginPath();
          let started = false;
          for (let i = 0; i < path.length; i++) {
            const s = shapeById.get(path[i]);
            if (!s) continue;
            const cx = (s.x + s.width / 2 - minX) * scale;
            const cy = (s.y + s.height / 2 - minY) * scale;
            if (!started) { ctx.moveTo(cx, cy); started = true; }
            else ctx.lineTo(cx, cy);
          }
          ctx.stroke();
        }
        ctx.globalAlpha = 1;
      }

      return new Promise((resolve, reject) => {
        offscreen.toBlob(blob => blob ? resolve(blob) : reject(new Error('Failed to create blob')), 'image/png');
      });
    }
  }), []);
 
  // rAF throttling and shared pending state
  const moveRafRef = useRef(0);
  const wheelRafRef = useRef(0);
  const pendingRef = useRef({ pan: null, drag: null, resize: null, selection: null, mouse: null });
 
  // Refs to avoid stale closures in high-frequency handlers
  const zoomRef = useRef(1);
  const panXRef = useRef(0);
  const panYRef = useRef(0);
  const applyResizeTransformationRef = useRef(null);
 
  // Helper: robust pointer position relative to the PIXI canvas
  const getPointerPos = useCallback((event) => {
    try {
      const stage = stageRef.current;
      const view = stage?.view || stage?.canvas;
      const clientX = (event && (event.clientX ?? event.data?.originalEvent?.clientX ?? event.nativeEvent?.clientX)) ?? event?.data?.global?.x;
      const clientY = (event && (event.clientY ?? event.data?.originalEvent?.clientY ?? event.nativeEvent?.clientY)) ?? event?.data?.global?.y;
      if (clientX != null && clientY != null && view && view.getBoundingClientRect) {
        const rect = view.getBoundingClientRect();
        return { x: clientX - rect.left, y: clientY - rect.top };
      }
      if (event?.data?.global) {
        return { x: event.data.global.x, y: event.data.global.y };
      }
    } catch {}
    return { x: 0, y: 0 };
  }, []);

  const flushMoveFrame = useCallback(() => {
    const pending = pendingRef.current;
    if (pending.pan) {
      setPanX(pending.pan.x);
      setPanY(pending.pan.y);
      pending.pan = null;
    }
    if (pending.drag) {
      setDragOffset(pending.drag);
      pending.drag = null;
    }
    if (pending.resize) {
      setResizeOffset(pending.resize);
      pending.resize = null;
    }
    if (pending.selection) {
      setCurrentSelectionRect(pending.selection);
      pending.selection = null;
    }
    if (pending.mouse) {
      setMousePosition(pending.mouse);
      pending.mouse = null;
    }
    moveRafRef.current = 0;
  }, []);
  const [isSelecting, setIsSelecting] = useState(false);
  const selectionAnchorRef = useRef({ x: 0, y: 0 }); // ref — always current, no stale closure
  const [currentSelectionRect, setCurrentSelectionRect] = useState(null);
  const [isCtrlPressed, setIsCtrlPressed] = useState(false);
  const [previewSelectedIds, setPreviewSelectedIds] = useState([]);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [justCompletedSelection, setJustCompletedSelection] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);

  // Drag state for moving shapes
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragOffset, setDragOffset] = useState(null);
  const [originalPositions, setOriginalPositions] = useState(new Map());
  
  // Resize state for bounding box
  const [isResizing, setIsResizing] = useState(false);
  const isResizingRef = useRef(false);
  // Group-rotation state for the shapes bounding box
  const isRotatingShapesRef = useRef(false);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0 });
  const [resizeAnchor, setResizeAnchor] = useState(null);
  const [originalBounds, setOriginalBounds] = useState({});
  const [resizeOffset, setResizeOffset] = useState({ x: 0, y: 0 });
  const resizeStartRef = useRef({ x: 0, y: 0 });
  const resizeCursorPosRef = useRef(null);
  const globalResizeHandlersRef = useRef({ move: null, up: null });

  // Background image transform state
  const [bgTransform, setBgTransform] = useState(null);
  const [bgSelected, setBgSelected] = useState(false);
  const bgTransformRef = useRef(null);
  const bgSelectedRef = useRef(false);
  const bgDragRef = useRef(null);
  const bgLockedRef = useRef(bgLocked);
  useEffect(() => {
    bgLockedRef.current = bgLocked;
    if (bgLocked) {
      setBgSelected(false);
      bgSelectedRef.current = false;
    }
  }, [bgLocked]);
  const bgResizeRef = useRef(null);
  const bgRotateRef = useRef(null);
  const bgInitRef = useRef(null);
  // Set true the moment the pointer goes down — prevents the hover handler from
  // overwriting 'grabbing' during the setTimeout(0) gap in handleMouseDown.
  const pointerDownRef = useRef(false);

  // Ref mirror of selectedBounds — readable synchronously in DOM event handlers
  const selectedBoundsRef = useRef(null);

  // Imperative cursor helper — sets cursor on the editor div directly so it
  // cannot be overridden by React's inline-style reconciliation.
  const setEditorCursor = useCallback((cursor) => {
    if (editorRef.current) editorRef.current.style.cursor = cursor;
  }, []);
  
  // Zoom and pan state
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);

  // Fit all shapes into the visible canvas
  const fitToContent = useCallback((shapesToFit, canvasW, canvasH) => {
    if (!shapesToFit || shapesToFit.length === 0) return;
    const PADDING = 40;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const s of shapesToFit) {
      if (s.x < minX) minX = s.x;
      if (s.y < minY) minY = s.y;
      if (s.x + s.width > maxX) maxX = s.x + s.width;
      if (s.y + s.height > maxY) maxY = s.y + s.height;
    }
    const contentW = maxX - minX + PADDING * 2;
    const contentH = maxY - minY + PADDING * 2;
    const fitZoom = Math.min(canvasW / contentW, canvasH / contentH, 5);
    setZoom(fitZoom);
    setPanX((canvasW - contentW * fitZoom) / 2 - (minX - PADDING) * fitZoom);
    setPanY((canvasH - contentH * fitZoom) / 2 - (minY - PADDING) * fitZoom);
  }, []);

  // Fit to content on initial load (once shapes arrive)
  const hasFittedRef = useRef(false);
  useEffect(() => {
    if (!hasFittedRef.current && shapes && shapes.length > 0 && width && height) {
      hasFittedRef.current = true;
      fitToContent(shapes, width, height);
    }
  }, [shapes, width, height, fitToContent]);
  
  // Debug-mode performance readout (FPS/memory) — only tracked while the
  // debug window is actually open, so it costs nothing otherwise.
  const [fps, setFps] = useState(0);
  const [memoryUsage, setMemoryUsage] = useState(0);
  useEffect(() => {
    if (!debugMode) return;
    const frameCountRef = { current: 0 };
    let lastTime = performance.now();
    let rafId;
    const tick = () => {
      frameCountRef.current += 1;
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    const id = setInterval(() => {
      const now = performance.now();
      const elapsed = now - lastTime;
      if (elapsed >= 500) {
        setFps(Math.round((frameCountRef.current * 1000) / elapsed));
        frameCountRef.current = 0;
        lastTime = now;
      }
      if (performance.memory) {
        setMemoryUsage(Math.round(performance.memory.usedJSHeapSize / 1024 / 1024 * 100) / 100);
      }
    }, 500);
    return () => {
      cancelAnimationFrame(rafId);
      clearInterval(id);
    };
  }, [debugMode]);

  // Viewport panning state
  const [isPanning, setIsPanning] = useState(false);
  const [panStartPos, setPanStartPos] = useState({ x: 0, y: 0 });
  const [panStartOffset, setPanStartOffset] = useState({ x: 0, y: 0 });

  // Tracks a pending left-button down so we can distinguish click vs drag
  const leftDownRef = useRef(null);

  // Keep refs in sync
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  useEffect(() => { panXRef.current = panX; panYRef.current = panY; }, [panX, panY]);
  useEffect(() => { bgTransformRef.current = bgTransform; }, [bgTransform]);

  // Refs for the stuck-drag failsafe below — read synchronously, no stale closures.
  const isDraggingRef = useRef(false);
  const dragOffsetRef = useRef(null);
  const originalPositionsRef = useRef(new Map());
  const isSelectingRef = useRef(false);
  const isPanningRef = useRef(false);
  useEffect(() => { isDraggingRef.current = isDragging; }, [isDragging]);
  useEffect(() => { dragOffsetRef.current = dragOffset; }, [dragOffset]);
  useEffect(() => { originalPositionsRef.current = originalPositions; }, [originalPositions]);
  useEffect(() => { isSelectingRef.current = isSelecting; }, [isSelecting]);
  useEffect(() => { isPanningRef.current = isPanning; }, [isPanning]);

  // Failsafe: shape drag/marquee-select/pan all rely on PixiJS's onMouseMove/
  // onMouseUp (bound to the canvas), which never fires if the pointer is
  // released outside the canvas bounds — leaving isDragging/dragOffset (or
  // isSelecting, or isPanning) stuck. A stuck non-null dragOffset then gets
  // applied to whatever shape is selected next, making it appear to "jump".
  // A window-level pointerup, checked one tick later (after Pixi's own
  // handler would have already run for an in-canvas release), catches the
  // cases Pixi misses without double-handling the normal case.
  useEffect(() => {
    const onWindowPointerUp = () => {
      setTimeout(() => {
        if (isDraggingRef.current) {
          const finalOffset = pendingRef.current.drag ?? dragOffsetRef.current;
          pendingRef.current.drag = null;
          const positionUpdates = [];
          selectedIdsRef.current.forEach(id => {
            const originalPos = originalPositionsRef.current.get(id);
            if (originalPos) {
              positionUpdates.push({
                id,
                x: originalPos.x + (finalOffset?.x ?? 0),
                y: originalPos.y + (finalOffset?.y ?? 0)
              });
            }
          });
          if (onUpdateShapePositions && positionUpdates.length > 0) {
            onUpdateShapePositions(positionUpdates);
            if (onCommitShapes) onCommitShapes(positionUpdates);
          }
          setIsDragging(false);
          setDragStart({ x: 0, y: 0 });
          setDragOffset(null);
          setOriginalPositions(new Map());
          leftDownRef.current = null;
        }
        if (isSelectingRef.current) {
          // Treat an off-canvas release as a cancelled selection rather than
          // trying to recompute intersecting shapes from a possibly-stale rect.
          setIsSelecting(false);
          setCurrentSelectionRect(null);
          setPreviewSelectedIds([]);
          leftDownRef.current = null;
        }
        if (isPanningRef.current) {
          setIsPanning(false);
        }
      }, 0);
    };
    window.addEventListener('pointerup', onWindowPointerUp);
    return () => window.removeEventListener('pointerup', onWindowPointerUp);
  }, [onUpdateShapePositions, onCommitShapes]);

  // Native contextmenu listener — most reliable way to detect right-click + position
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const handler = (e) => {
      e.preventDefault(); // always suppress browser context menu
      if (!viewOnlyRef.current) return;
      const cb = onContextMenuRef.current;
      if (!cb) return;
      const rect = el.getBoundingClientRect();
      const canvasX = e.clientX - rect.left;
      const canvasY = e.clientY - rect.top;
      const sx = (canvasX - panXRef.current) / (zoomRef.current || 1);
      const sy = (canvasY - panYRef.current) / (zoomRef.current || 1);
      // Hit-test shapes
      const shapes = shapesRef.current;
      let hit = null;
      for (let i = shapes.length - 1; i >= 0; i--) {
        const s = shapes[i];
        if (sx >= s.x && sx <= s.x + s.width && sy >= s.y && sy <= s.y + s.height) {
          hit = s;
          break;
        }
      }
      if (hit) {
        // Select the right-clicked chair first (if not already selected),
        // so the context menu acts on it — matches the old behavior.
        if (!selectedIdsRef.current?.includes(hit.id)) {
          onSelectRef.current?.(hit.id);
        }
        cb(hit.id, { evt: { clientX: e.clientX, clientY: e.clientY } });
      }
    };
    el.addEventListener('contextmenu', handler);
    return () => el.removeEventListener('contextmenu', handler);
  }, []); // stable — reads everything via refs

  // Show grabbing cursor while panning
  useEffect(() => {
    if (isPanning) setEditorCursor('grabbing');
    // When panning ends the mousemove hover-detect below will restore the right cursor
  }, [isPanning, setEditorCursor]);

  // Hover-detect: show 'grab' cursor when the pointer is over any resize anchor
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    el.style.cursor = 'default'; // initialize once on mount
    const HIT_SHAPE = 10;
    const HIT_BG    = 12;
    const HIT_ROT   = 14;
    const ROT_OFFSET = 32;
    // Returns stage-space position of the rotation handle for a given bgTransform
    const rotHandleStagePos = (bt) => {
      const rot = bt.rot ?? 0;
      const cx = bt.x + bt.w / 2;
      const cy = bt.y + bt.h / 2;
      // image-local: (w/2, -ROT_OFFSET) => offset from center: (0, -(h/2 + ROT_OFFSET))
      const lx = 0;
      const ly = -(bt.h / 2 + ROT_OFFSET);
      return {
        x: cx + lx * Math.cos(rot) - ly * Math.sin(rot),
        y: cy + lx * Math.sin(rot) + ly * Math.cos(rot),
      };
    };
    const onDown = () => { pointerDownRef.current = true; };
    const onUpGlobal = () => { pointerDownRef.current = false; };
    el.addEventListener('pointerdown', onDown);
    window.addEventListener('pointerup', onUpGlobal);
    const onMove = (e) => {
      // Suppress cursor changes while a button is held OR an active drag is running
      if (pointerDownRef.current || isResizingRef.current || isRotatingShapesRef.current || bgResizeRef.current !== null || bgDragRef.current !== null || bgRotateRef.current !== null) return;
      const rect = el.getBoundingClientRect();
      const canvasX = e.clientX - rect.left;
      const canvasY = e.clientY - rect.top;
      const sx = (canvasX - panXRef.current) / (zoomRef.current || 1);
      const sy = (canvasY - panYRef.current) / (zoomRef.current || 1);
      // 'default' | resize cursor | ROTATE_CURSOR
      let nextCursor = 'default';
      // Shape anchors → directional resize cursor
      const sb = selectedBoundsRef.current;
      if (sb) {
        const { x: bx, y: by, width: bw, height: bh } = sb;
        const pts = [
          { id: 'nw', x: bx,          y: by          }, { id: 'n', x: bx + bw / 2, y: by          }, { id: 'ne', x: bx + bw, y: by          },
          { id: 'e',  x: bx + bw,     y: by + bh / 2 }, { id: 'se', x: bx + bw,     y: by + bh     }, { id: 's', x: bx + bw / 2, y: by + bh },
          { id: 'sw', x: bx,          y: by + bh     }, { id: 'w', x: bx,           y: by + bh / 2 },
        ];
        const hit = pts.find(a => Math.abs(sx - a.x) <= HIT_SHAPE && Math.abs(sy - a.y) <= HIT_SHAPE);
        if (hit) {
          nextCursor = RESIZE_ANCHOR_CURSORS[hit.id];
        } else {
          const rotX = bx + bw / 2;
          const rotY = by - SHAPE_ROT_OFFSET;
          if (Math.abs(sx - rotX) <= SHAPE_ROT_HIT && Math.abs(sy - rotY) <= SHAPE_ROT_HIT) {
            nextCursor = ROTATE_CURSOR;
          }
        }
      }
      // Bg image handles
      if (nextCursor === 'default') {
        const bt = bgTransformRef.current;
        if (bgSelectedRef.current && bt) {
          // Rotation handle → rotate cursor
          const rh = rotHandleStagePos(bt);
          if (Math.sqrt((sx - rh.x) ** 2 + (sy - rh.y) ** 2) <= HIT_ROT) {
            nextCursor = ROTATE_CURSOR;
          } else {
            // Resize handles (rotation-aware) → 'grab'
            const rot = bt.rot ?? 0;
            const imageCx = bt.x + bt.w / 2;
            const imageCy = bt.y + bt.h / 2;
            const dx = sx - imageCx;
            const dy = sy - imageCy;
            const localX = dx * Math.cos(-rot) - dy * Math.sin(-rot) + bt.w / 2;
            const localY = dx * Math.sin(-rot) + dy * Math.cos(-rot) + bt.h / 2;
            const pts = [
              { x: 0,         y: 0         }, { x: bt.w,       y: 0         },
              { x: bt.w,      y: bt.h      }, { x: 0,           y: bt.h      },
              { x: bt.w / 2,  y: 0         }, { x: bt.w / 2,    y: bt.h      },
              { x: bt.w,      y: bt.h / 2  }, { x: 0,           y: bt.h / 2  },
            ];
            if (pts.some(h => Math.abs(localX - h.x) <= HIT_BG && Math.abs(localY - h.y) <= HIT_BG)) {
              nextCursor = 'grab';
            } else if (localX >= 0 && localX <= bt.w && localY >= 0 && localY <= bt.h) {
              // Interior of bg image → show grab to indicate it's draggable
              nextCursor = 'grab';
            }
          }
        }
      }
      el.style.cursor = nextCursor;
    };
    el.addEventListener('mousemove', onMove);
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointerup', onUpGlobal);
    };
  }, []); // empty deps — uses only stable refs

  // Initialise bgTransform whenever backgroundImage or its saved transform changes.
  // bgInitRef tracks the last (backgroundImage, bgImageInitialTransform) pair we applied
  // so we don't overwrite a user's live edits on unrelated re-renders.
  useEffect(() => {
    if (!backgroundImage) {
      bgInitRef.current = null;
      setBgTransform(null);
      bgTransformRef.current = null;
      setBgSelected(false);
      bgSelectedRef.current = false;
      return;
    }
    const initKey = JSON.stringify(bgImageInitialTransform);
    if (backgroundImage === bgInitRef.current?.img && initKey === bgInitRef.current?.key) return;
    bgInitRef.current = { img: backgroundImage, key: initKey };
    const init = bgImageInitialTransform || { x: 0, y: 0, w: width, h: height };
    setBgTransform(init);
    bgTransformRef.current = init;
    setBgSelected(false);
    bgSelectedRef.current = false;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backgroundImage, bgImageInitialTransform]);
 
  // Cleanup any scheduled rAF on unmount
  useEffect(() => {
    return () => {
      if (moveRafRef.current) cancelAnimationFrame(moveRafRef.current);
      if (wheelRafRef.current) cancelAnimationFrame(wheelRafRef.current);
    };
  }, []);
  
  // Track Ctrl key state
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey) {
        setIsCtrlPressed(true);
      }
    };
    
    const handleKeyUp = (e) => {
      if (!e.ctrlKey && !e.metaKey) {
        setIsCtrlPressed(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Handle wheel events for zoom - attach to editor container
  useEffect(() => {
    const editorElement = editorRef.current;
    if (!editorElement) return;

    const handleWheelEvent = (e) => {
      if (isLocked) return;
      if (!viewOnly && !(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      e.stopPropagation();

      const currentZoom = zoomRef.current;
      const currentPanX = panXRef.current;
      const currentPanY = panYRef.current;

      const delta = e.deltaY;
      const factor = delta > 0 ? 0.9 : 1 / 0.9;
      const nextZoom = Math.max(0.02, Math.min(10, currentZoom * factor));
      if (nextZoom === currentZoom) return;

      const rect = editorElement.getBoundingClientRect();
      const mousePos = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      const zoomPoint = { x: (mousePos.x - currentPanX) / currentZoom, y: (mousePos.y - currentPanY) / currentZoom };
      const nextPanX = mousePos.x - zoomPoint.x * nextZoom;
      const nextPanY = mousePos.y - zoomPoint.y * nextZoom;

      if (wheelRafRef.current) cancelAnimationFrame(wheelRafRef.current);
      wheelRafRef.current = requestAnimationFrame(() => {
        setZoom(nextZoom);
        setPanX(nextPanX);
        setPanY(nextPanY);
      });
    };

    editorElement.addEventListener('wheel', handleWheelEvent, { passive: false });

    return () => {
      if (editorElement) {
        editorElement.removeEventListener('wheel', handleWheelEvent);
      }
    };
  }, [zoom, panX, panY, isLocked, viewOnly]);


  // O(1) shape lookup map — used by hit-testing and ScanPathLines
  const shapeMap = useMemo(() => {
    const m = new Map();
    shapes.forEach(s => m.set(s.id, s));
    return m;
  }, [shapes]);

  // Viewport culling — only pass shapes that overlap the visible canvas area.
  // We add a margin equal to the largest possible shape dimension (128px) so
  // partially-visible shapes are never clipped.
  const visibleShapes = useMemo(() => {
    const MARGIN = 128;
    const viewLeft   = (-panX / zoom) - MARGIN;
    const viewTop    = (-panY / zoom) - MARGIN;
    const viewRight  = (width  / zoom) - (panX / zoom) + MARGIN;
    const viewBottom = (height / zoom) - (panY / zoom) + MARGIN;
    return shapes.filter(s =>
      s.x < viewRight  && (s.x + s.width)  > viewLeft &&
      s.y < viewBottom && (s.y + s.height) > viewTop
    );
  }, [shapes, panX, panY, zoom, width, height]);

  // JS hit-test: returns the topmost shape under stage-coordinates (sx, sy)
  const hitTestShape = useCallback((sx, sy) => {
    // Iterate in reverse so shapes drawn last (on top) win
    for (let i = shapes.length - 1; i >= 0; i--) {
      const s = shapes[i];
      if (!s.rotation) {
        if (sx >= s.x && sx <= s.x + s.width && sy >= s.y && sy <= s.y + s.height) {
          return s;
        }
        continue;
      }
      // Rotated shape: transform the point into the shape's local (unrotated) space
      const cx = s.x + s.width / 2, cy = s.y + s.height / 2;
      const rad = -s.rotation * Math.PI / 180;
      const dx = sx - cx, dy = sy - cy;
      const lx = dx * Math.cos(rad) - dy * Math.sin(rad) + s.width / 2;
      const ly = dx * Math.sin(rad) + dy * Math.cos(rad) + s.height / 2;
      if (lx >= 0 && lx <= s.width && ly >= 0 && ly <= s.height) {
        return s;
      }
    }
    return null;
  }, [shapes]);

  // Calculate bounding box for selected shapes
  const selectedBounds = useMemo(() => {
    if (selectedIds.length === 0) return null;

    // Use visibleShapes (viewport-culled) so the box only covers shapes that are
    // actually rendered with red selection borders — not off-screen selected shapes.
    const selectedSet = new Set(selectedIds);
    const selectedShapes = visibleShapes.filter(shape => selectedSet.has(shape.id));
    if (selectedShapes.length === 0) return null;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    const ox = dragOffset?.x ?? 0;
    const oy = dragOffset?.y ?? 0;

    selectedShapes.forEach(shape => {
      const sx = shape.x + ox;
      const sy = shape.y + oy;
      // Rotated shapes: enclose their actual (tilted) corners, not the
      // unrotated x/y/width/height box.
      const corners = rotatedRectPoints(sx, sy, shape.width, shape.height, shape.rotation || 0);
      for (let i = 0; i < corners.length; i += 2) {
        minX = Math.min(minX, corners[i]);
        maxX = Math.max(maxX, corners[i]);
        minY = Math.min(minY, corners[i + 1]);
        maxY = Math.max(maxY, corners[i + 1]);
      }
    });

    // Outset by 2px to match the selection border drawn around each shape
    return {
      x: minX - 2,
      y: minY - 2,
      width: maxX - minX + 4,
      height: maxY - minY + 4,
    };
  }, [selectedIds, visibleShapes, dragOffset]);

  // Keep ref in sync so handleMouseDown can read it without stale closures
  useEffect(() => { selectedBoundsRef.current = selectedBounds; }, [selectedBounds]);

  // Resize HUD info (anchor mode and preview sizes)
  // ─── Background image drag / resize ────────────────────────────────────────
  // Start a bg-corner resize from a canvas-relative screen position.
  // Called from the DOM setTimeout handler — no PixiJS events needed.
  const startBgCornerResize = useCallback((anchor, startScreenPos) => {
    if (!bgTransformRef.current) return;
    bgResizeRef.current = { startScreen: startScreenPos, originTransform: { ...bgTransformRef.current }, anchor };
    setEditorCursor('grabbing');
    const getRect = () => editorRef.current?.getBoundingClientRect();
    const onMove = (ev) => {
      if (!bgResizeRef.current) return;
      const rect = getRect(); if (!rect) return;
      const p = { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
      const dx = (p.x - bgResizeRef.current.startScreen.x) / (zoomRef.current || 1);
      const dy = (p.y - bgResizeRef.current.startScreen.y) / (zoomRef.current || 1);
      const { x, y, w, h } = bgResizeRef.current.originTransform;
      const aspect = w / h;
      const a = bgResizeRef.current.anchor;
      let nx = x, ny = y, nw = w, nh = h;
      // Corners: aspect-ratio locked
      if (a === 'nw') {
        const d = (Math.abs(dx) > Math.abs(dy)) ? dx : dy;
        nw = Math.max(20, w - d); nh = nw / aspect;
        nx = x + (w - nw); ny = y + (h - nh);
      } else if (a === 'ne') {
        const d = (Math.abs(dx) > Math.abs(dy)) ? dx : -dy;
        nw = Math.max(20, w + d); nh = nw / aspect;
        ny = y + (h - nh);
      } else if (a === 'se') {
        const d = (Math.abs(dx) > Math.abs(dy)) ? dx : dy;
        nw = Math.max(20, w + d); nh = nw / aspect;
      } else if (a === 'sw') {
        const d = (Math.abs(dx) > Math.abs(dy)) ? -dx : dy;
        nw = Math.max(20, w + d); nh = nw / aspect;
        nx = x + (w - nw);
      // Edges: single-axis crop (free, no ratio)
      } else if (a === 'n') {
        nh = Math.max(20, h - dy); ny = y + (h - nh);
      } else if (a === 's') {
        nh = Math.max(20, h + dy);
      } else if (a === 'e') {
        nw = Math.max(20, w + dx);
      } else if (a === 'w') {
        nw = Math.max(20, w - dx); nx = x + (w - nw);
      }
      const t = { x: nx, y: ny, w: nw, h: nh, rot: bgResizeRef.current.originTransform.rot ?? 0 };
      setBgTransform(t);
      bgTransformRef.current = t;
    };
    const onUp = () => {
      if (onBgTransformCommit && bgTransformRef.current) {
        onBgTransformCommit(bgTransformRef.current);
      }
      setEditorCursor('default');
      bgResizeRef.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [onBgTransformCommit, setEditorCursor]);

  // Start a bg image rotation drag from a canvas-relative screen position.
  const startBgRotate = useCallback((startScreenPos) => {
    const bt = bgTransformRef.current;
    if (!bt) return;
    // Center of the image in canvas (screen) coordinates
    const centerCanvasX = (bt.x + bt.w / 2) * (zoomRef.current || 1) + panXRef.current;
    const centerCanvasY = (bt.y + bt.h / 2) * (zoomRef.current || 1) + panYRef.current;
    const startAngle = Math.atan2(startScreenPos.y - centerCanvasY, startScreenPos.x - centerCanvasX);
    bgRotateRef.current = { centerCanvasX, centerCanvasY, startAngle, originRot: bt.rot ?? 0 };
    setEditorCursor('grabbing');
    const getRect = () => editorRef.current?.getBoundingClientRect();
    const onMove = (ev) => {
      if (!bgRotateRef.current) return;
      const r = getRect(); if (!r) return;
      const p = { x: ev.clientX - r.left, y: ev.clientY - r.top };
      const { centerCanvasX: ccx, centerCanvasY: ccy, startAngle: sa, originRot } = bgRotateRef.current;
      const currentAngle = Math.atan2(p.y - ccy, p.x - ccx);
      const newRot = originRot + (currentAngle - sa);
      const t = { ...bgTransformRef.current, rot: newRot };
      setBgTransform(t);
      bgTransformRef.current = t;
    };
    const onUp = () => {
      if (onBgTransformCommit && bgTransformRef.current) {
        onBgTransformCommit(bgTransformRef.current);
      }
      setEditorCursor('default');
      bgRotateRef.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [onBgTransformCommit, setEditorCursor]);

  // Handle stage right-click for deselection
  // Context menu suppression handled in handleMouseDown via preventDefault

  // Start a shape-anchor resize from a canvas-relative screen position.
  // Called from the DOM setTimeout handler — no PixiJS events needed.
  const startShapeAnchorResize = useCallback((anchor, startScreenPos) => {
    isResizingRef.current = true;
    setIsResizing(true);
    setEditorCursor('crosshair');
    setResizeStart(startScreenPos);
    resizeStartRef.current = startScreenPos;
    setResizeAnchor(anchor);

    const bounds = {};
    selectedIds.forEach(id => {
      const shape = shapeMap.get(id);
      if (shape) bounds[id] = { x: shape.x, y: shape.y, width: shape.width, height: shape.height };
    });
    setOriginalBounds(bounds);

    const onPointerMove = (e) => {
      const stage = stageRef.current;
      const view = stage?.view || stage?.canvas;
      if (!view) return;
      const rect = view.getBoundingClientRect();
      const pos2 = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      resizeCursorPosRef.current = pos2;
      const nextOffset = {
        x: (pos2.x - resizeStartRef.current.x) / (zoomRef.current || 1),
        y: (pos2.y - resizeStartRef.current.y) / (zoomRef.current || 1),
      };
      pendingRef.current.resize = nextOffset;
      if (!moveRafRef.current) moveRafRef.current = requestAnimationFrame(flushMoveFrame);
    };
    const onPointerUp = (e) => {
      const stage = stageRef.current;
      const view = stage?.view || stage?.canvas;
      let finalOffset = { x: 0, y: 0 };
      try {
        if (view) {
          const rect = view.getBoundingClientRect();
          const pos2 = { x: e.clientX - rect.left, y: e.clientY - rect.top };
          finalOffset = {
            x: (pos2.x - resizeStartRef.current.x) / (zoomRef.current || 1),
            y: (pos2.y - resizeStartRef.current.y) / (zoomRef.current || 1),
          };
        }
      } catch {}
      if (applyResizeTransformationRef.current) applyResizeTransformationRef.current(finalOffset, anchor, true);
      setIsResizing(false);
      isResizingRef.current = false;
      setEditorCursor('default');
      setResizeAnchor(null);
      setOriginalBounds({});
      setResizeOffset({ x: 0, y: 0 });
      resizeCursorPosRef.current = null;
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      globalResizeHandlersRef.current = { move: null, up: null };
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    globalResizeHandlersRef.current = { move: onPointerMove, up: onPointerUp };
  }, [selectedIds, shapeMap, flushMoveFrame, setEditorCursor]);

  // Start a group rotation drag for the selected shapes from a canvas-relative
  // screen position. Mirrors startBgRotate, but rotates every selected shape
  // around the selection's bounding-box center and updates each shape's own
  // `rotation` too (so the shape itself visibly tilts, not just its position).
  const startShapeRotate = useCallback((startScreenPos) => {
    const sb = selectedBoundsRef.current;
    if (!sb || selectedIds.length === 0) return;

    const pivotX = sb.x + sb.width / 2;
    const pivotY = sb.y + sb.height / 2;
    const centerCanvasX = pivotX * (zoomRef.current || 1) + panXRef.current;
    const centerCanvasY = pivotY * (zoomRef.current || 1) + panYRef.current;
    const startAngle = Math.atan2(startScreenPos.y - centerCanvasY, startScreenPos.x - centerCanvasX);

    const originalShapes = {};
    selectedIds.forEach(id => {
      const shape = shapeMap.get(id);
      if (shape) {
        originalShapes[id] = { x: shape.x, y: shape.y, width: shape.width, height: shape.height, rotation: shape.rotation || 0 };
      }
    });

    isRotatingShapesRef.current = true;
    setEditorCursor(ROTATE_CURSOR);

    const applyRotation = (rawDeltaDeg, isCommit) => {
      // Snap to a multiple of 90° once the drag is within a couple of degrees of one.
      const nearest90 = Math.round(rawDeltaDeg / 90) * 90;
      const deltaDeg = Math.abs(rawDeltaDeg - nearest90) <= SHAPE_ROT_SNAP_THRESHOLD ? nearest90 : rawDeltaDeg;
      const rad = deltaDeg * Math.PI / 180;
      const cos = Math.cos(rad), sin = Math.sin(rad);
      const transformed = selectedIds.map(id => {
        const o = originalShapes[id];
        if (!o) return null;
        const cx0 = o.x + o.width / 2;
        const cy0 = o.y + o.height / 2;
        const dx = cx0 - pivotX, dy = cy0 - pivotY;
        const cx1 = pivotX + dx * cos - dy * sin;
        const cy1 = pivotY + dx * sin + dy * cos;
        return {
          id,
          x: cx1 - o.width / 2,
          y: cy1 - o.height / 2,
          rotation: o.rotation + deltaDeg,
        };
      }).filter(Boolean);
      if (onUpdateShapePositions && transformed.length > 0) onUpdateShapePositions(transformed);
      if (isCommit && onCommitShapes && transformed.length > 0) onCommitShapes(transformed);
    };

    const angleFromEvent = (ev) => {
      const view = stageRef.current?.view || stageRef.current?.canvas;
      const rect = view?.getBoundingClientRect();
      if (!rect) return null;
      const p = { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
      return Math.atan2(p.y - centerCanvasY, p.x - centerCanvasX);
    };

    const onMove = (ev) => {
      if (!isRotatingShapesRef.current) return;
      const currentAngle = angleFromEvent(ev);
      if (currentAngle === null) return;
      applyRotation((currentAngle - startAngle) * 180 / Math.PI, false);
    };
    const onUp = (ev) => {
      const currentAngle = angleFromEvent(ev);
      applyRotation(currentAngle === null ? 0 : (currentAngle - startAngle) * 180 / Math.PI, true);
      isRotatingShapesRef.current = false;
      setEditorCursor('default');
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [selectedIds, shapeMap, onUpdateShapePositions, onCommitShapes, setEditorCursor]);

  // Handle drag start from shape component
  // Handle resize start
  const handleResizeStart = useCallback((anchor, event) => {
    // Check if this is a left mouse button click (button 0)
    let isLeftClick = false;
    try {
      if (event.data && event.data.originalEvent) {
        isLeftClick = event.data.originalEvent.button === 0;
      } else if (event.nativeEvent) {
        isLeftClick = event.nativeEvent.button === 0;
      }
    } catch (error) {
      // Default to left click if we can't determine
      isLeftClick = true;
    }
    
    if (!isLeftClick) {
      return;
    }
    
    const pos = getPointerPos(event);
    
    isResizingRef.current = true;
    setIsResizing(true);
    setResizeStart(pos);
    resizeStartRef.current = pos;
    setResizeAnchor(anchor);
    
    // Store original bounds of all selected shapes
    const bounds = {};
    selectedIds.forEach(id => {
      const shape = shapeMap.get(id);
      if (shape) {
        bounds[id] = { x: shape.x, y: shape.y, width: shape.width, height: shape.height };
      }
    });
    setOriginalBounds(bounds);
    
    // Attach global pointer tracking to ensure continuous updates outside anchors/stage
    const onPointerMove = (e) => {
      const stage = stageRef.current;
      const view = stage?.view || stage?.canvas;
      if (!view) return;
      const rect = view.getBoundingClientRect();
      const pos2 = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      resizeCursorPosRef.current = pos2;
      const nextOffset = {
        x: (pos2.x - resizeStartRef.current.x) / (zoomRef.current || 1),
        y: (pos2.y - resizeStartRef.current.y) / (zoomRef.current || 1)
      };
      pendingRef.current.resize = nextOffset;
      if (!moveRafRef.current) {
        moveRafRef.current = requestAnimationFrame(flushMoveFrame);
      }
    };
    const onPointerUp = (e) => {
      // Finalize resize using the latest offset
      const stage = stageRef.current;
      const view = stage?.view || stage?.canvas;
      let finalOffset = resizeOffset;
      try {
        if (view) {
          const rect = view.getBoundingClientRect();
          const pos2 = { x: e.clientX - rect.left, y: e.clientY - rect.top };
          finalOffset = {
            x: (pos2.x - resizeStartRef.current.x) / (zoomRef.current || 1),
            y: (pos2.y - resizeStartRef.current.y) / (zoomRef.current || 1)
          };
        }
      } catch {}
      // Use ref to avoid initialization order issues
      if (applyResizeTransformationRef.current) {
        applyResizeTransformationRef.current(finalOffset, anchor);
      }
      setIsResizing(false);
      isResizingRef.current = false;
      setResizeAnchor(null);
      setOriginalBounds({});
      setResizeOffset({ x: 0, y: 0 });
      resizeCursorPosRef.current = null;
      // Remove listeners
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      globalResizeHandlersRef.current = { move: null, up: null };
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    globalResizeHandlersRef.current = { move: onPointerMove, up: onPointerUp };
  }, [selectedIds, shapeMap, getPointerPos, flushMoveFrame]);

  // Apply resize transformation to shapes
  const applyResizeTransformation = useCallback((resizeOffset, anchor, isCommit = false) => {
    if (selectedIds.length === 0 || Object.keys(originalBounds).length === 0) return;

    // Compute the reference bounding box from originalBounds (the per-shape snapshot taken
    // at resize-start).  This is stable for the entire drag — unlike selectedBounds which
    // gets recomputed from the live-updated shapes after every frame, causing compounding
    // scale errors.
    let oMinX = Infinity, oMinY = Infinity, oMaxX = -Infinity, oMaxY = -Infinity;
    selectedIds.forEach(id => {
      const b = originalBounds[id];
      if (b) {
        oMinX = Math.min(oMinX, b.x);        oMinY = Math.min(oMinY, b.y);
        oMaxX = Math.max(oMaxX, b.x + b.width); oMaxY = Math.max(oMaxY, b.y + b.height);
      }
    });
    if (oMinX === Infinity) return;

    // Apply the same 2px outset used in selectedBounds so anchor positions stay consistent.
    const boundsX = oMinX - 2;
    const boundsY = oMinY - 2;
    const boundsWidth  = (oMaxX - oMinX) + 4;
    const boundsHeight = (oMaxY - oMinY) + 4;
    
    // Calculate scale factors based on anchor and offset
    let scaleX = 1, scaleY = 1;
    let offsetX = 0, offsetY = 0;
    
    switch (anchor) {
      case 'nw': // Top-left corner
        scaleX = (boundsWidth - resizeOffset.x) / boundsWidth;
        scaleY = (boundsHeight - resizeOffset.y) / boundsHeight;
        offsetX = resizeOffset.x;
        offsetY = resizeOffset.y;
        break;
      case 'n': // Top edge
        scaleY = (boundsHeight - resizeOffset.y) / boundsHeight;
        offsetY = resizeOffset.y;
        break;
      case 'ne': // Top-right corner
        scaleX = (boundsWidth + resizeOffset.x) / boundsWidth;
        scaleY = (boundsHeight - resizeOffset.y) / boundsHeight;
        offsetY = resizeOffset.y;
        break;
      case 'e': // Right edge
        scaleX = (boundsWidth + resizeOffset.x) / boundsWidth;
        break;
      case 'se': // Bottom-right corner
        scaleX = (boundsWidth + resizeOffset.x) / boundsWidth;
        scaleY = (boundsHeight + resizeOffset.y) / boundsHeight;
        break;
      case 's': // Bottom edge
        scaleY = (boundsHeight + resizeOffset.y) / boundsHeight;
        break;
      case 'sw': // Bottom-left corner
        scaleX = (boundsWidth - resizeOffset.x) / boundsWidth;
        scaleY = (boundsHeight + resizeOffset.y) / boundsHeight;
        offsetX = resizeOffset.x;
        break;
      case 'w': // Left edge
        scaleX = (boundsWidth - resizeOffset.x) / boundsWidth;
        offsetX = resizeOffset.x;
        break;
      default:
        return;
    }
    
    // Ensure minimum scale to prevent negative or zero sizes
    scaleX = Math.max(0.1, scaleX);
    scaleY = Math.max(0.1, scaleY);

    // Corner anchors: uniform scale, then recompute offsets so the opposite corner
    // stays pinned — otherwise the "fixed" corner drifts when aspect ratio is locked.
    if (anchor === 'nw' || anchor === 'ne' || anchor === 'se' || anchor === 'sw') {
      const uniform = Math.max(scaleX, scaleY);
      scaleX = uniform;
      scaleY = uniform;
      // Fixed corners:  nw→SE,  ne→SW,  se→NW,  sw→NE
      switch (anchor) {
        case 'nw': offsetX = boundsWidth  * (1 - uniform); offsetY = boundsHeight * (1 - uniform); break;
        case 'ne': offsetX = 0;                            offsetY = boundsHeight * (1 - uniform); break;
        case 'se': offsetX = 0;                            offsetY = 0;                            break;
        case 'sw': offsetX = boundsWidth  * (1 - uniform); offsetY = 0;                            break;
        default: break;
      }
    }
    
    // Apply transformation to each selected shape
    const transformedShapes = selectedIds.map(id => {
      const shapeOriginalBounds = originalBounds[id];
      if (!shapeOriginalBounds) return null;
      
      // Calculate new position relative to the bounding box center
      const relativeX = shapeOriginalBounds.x - boundsX;
      const relativeY = shapeOriginalBounds.y - boundsY;
      
      // Apply scaling and offset
      const newX = boundsX + offsetX + (relativeX * scaleX);
      const newY = boundsY + offsetY + (relativeY * scaleY);
      const newWidth = shapeOriginalBounds.width * scaleX;
      const newHeight = shapeOriginalBounds.height * scaleY;
      
      return {
        id,
        x: newX,
        y: newY,
        width: newWidth,
        height: newHeight
      };
    }).filter(Boolean);
    
    // Update shapes in real-time
    if (onUpdateShapePositions && transformedShapes.length > 0) {
      onUpdateShapePositions(transformedShapes);
    }
    // On final commit (pointerUp), push to undo history
    if (isCommit && onCommitShapes && transformedShapes.length > 0) {
      onCommitShapes(transformedShapes);
    }
  }, [selectedIds, originalBounds, onUpdateShapePositions, onCommitShapes]);

  // Keep a ref to avoid order-of-initialization issues in early callbacks
  useEffect(() => {
    applyResizeTransformationRef.current = applyResizeTransformation;
  }, [applyResizeTransformation]);

  // Live apply resize while dragging anchors (via ref to avoid init order issues)
  useEffect(() => {
    if (!isResizing || !resizeAnchor) return;
    if (applyResizeTransformationRef.current) {
      applyResizeTransformationRef.current(resizeOffset, resizeAnchor, false);
    }
  }, [isResizing, resizeOffset, resizeAnchor]);
  
  // Handle mouse down for rectangle selection and viewport panning
  const handleMouseDown = useCallback((event) => {
    if (isLocked) return;
    if (!viewOnly && isResizingRef.current) return;

    let isRightClick = false;
    let isCtrl = false;
    let pos = { x: 0, y: 0 };

    try {
      if (event.data && event.data.originalEvent) {
        isRightClick = event.data.originalEvent.button === 2;
        isCtrl = event.data.originalEvent.ctrlKey || event.data.originalEvent.metaKey;
        // Suppress browser context menu
        if (isRightClick) event.data.originalEvent.preventDefault();
        if (event.data.getLocalPosition) {
          pos = event.data.getLocalPosition(event.currentTarget);
        } else {
          const rect = event.currentTarget.getBoundingClientRect();
          pos = {
            x: event.data.originalEvent.clientX - rect.left,
            y: event.data.originalEvent.clientY - rect.top
          };
        }
      } else if (event.nativeEvent) {
        isRightClick = event.nativeEvent.button === 2;
        isCtrl = event.nativeEvent.ctrlKey || event.nativeEvent.metaKey;
        if (isRightClick) event.nativeEvent.preventDefault();
        const rect = event.currentTarget.getBoundingClientRect();
        pos = {
          x: event.nativeEvent.clientX - rect.left,
          y: event.nativeEvent.clientY - rect.top
        };
      }
    } catch (error) {
      pos = { x: 0, y: 0 };
    }

    if (isRightClick) {
      // Right button: start viewport panning (context menu is handled by native contextmenu event)
      setIsPanning(true);
      setPanStartPos(pos);
      setPanStartOffset({ x: panX, y: panY });
    } else if (viewOnly) {
      // viewOnly left-click: only select shapes, never pan
      const stageX = (pos.x - panX) / zoom;
      const stageY = (pos.y - panY) / zoom;
      const clickedShape = hitTestShape(stageX, stageY);
      leftDownRef.current = { pos, stageX, stageY, clickedShape, isCtrl: false, activated: false, isDragMove: false };
    } else {
      // Left button: defer so PixiJS anchor/corner handlers fire first (they use
      // pointerdown which is dispatched AFTER the DOM mousedown event).
      // By the time the timeout runs, isResizingRef.current will be true if an
      // anchor was clicked, and we simply skip the left-click logic.
      const capturedPos = pos;
      const capturedPanX = panX;
      const capturedPanY = panY;
      const capturedZoom = zoom;
      const capturedIsCtrl = isCtrl;
      setTimeout(() => {
        if (isResizingRef.current) return; // anchor took it

        const stageX = (capturedPos.x - capturedPanX) / capturedZoom;
        const stageY = (capturedPos.y - capturedPanY) / capturedZoom;

        // Hit-test shape group rotate handle (above the selection, like the bg image's)
        const sb = selectedBoundsRef.current;
        if (sb && selectedIds.length > 0) {
          const rotHandleX = sb.x + sb.width / 2;
          const rotHandleY = sb.y - SHAPE_ROT_OFFSET;
          if (Math.abs(stageX - rotHandleX) <= SHAPE_ROT_HIT && Math.abs(stageY - rotHandleY) <= SHAPE_ROT_HIT) {
            startShapeRotate(capturedPos);
            return;
          }
        }

        // Hit-test shape resize anchors (8 handles around selectedBounds)
        if (sb && selectedIds.length > 0) {
          const HIT = 10;
          const { x: bx, y: by, width: bw, height: bh } = sb;
          const shapeAnchors = [
            { id: 'nw', x: bx,            y: by             },
            { id: 'n',  x: bx + bw / 2,   y: by             },
            { id: 'ne', x: bx + bw,       y: by             },
            { id: 'e',  x: bx + bw,       y: by + bh / 2    },
            { id: 'se', x: bx + bw,       y: by + bh        },
            { id: 's',  x: bx + bw / 2,   y: by + bh        },
            { id: 'sw', x: bx,            y: by + bh        },
            { id: 'w',  x: bx,            y: by + bh / 2    },
          ];
          const hitAnchor = shapeAnchors.find(a =>
            stageX >= a.x - HIT && stageX <= a.x + HIT &&
            stageY >= a.y - HIT && stageY <= a.y + HIT
          );
          if (hitAnchor) {
            startShapeAnchorResize(hitAnchor.id, capturedPos);
            return;
          }
        }

        // Hit-test bg image rotation handle + corners/edges (rotation-aware)
        const bt = bgTransformRef.current;
        if (bgSelectedRef.current && bt) {
          const ROT_OFFSET = 32;
          const HIT_ROT = 12;
          const rot = bt.rot ?? 0;
          const imageCx = bt.x + bt.w / 2;
          const imageCy = bt.y + bt.h / 2;

          // Rotation handle: image-local offset (0, -(h/2 + ROT_OFFSET)) from center, then rotated
          const rhLocalX = 0;
          const rhLocalY = -(bt.h / 2 + ROT_OFFSET);
          const rhStageX = imageCx + rhLocalX * Math.cos(rot) - rhLocalY * Math.sin(rot);
          const rhStageY = imageCy + rhLocalX * Math.sin(rot) + rhLocalY * Math.cos(rot);
          if (Math.abs(stageX - rhStageX) <= HIT_ROT && Math.abs(stageY - rhStageY) <= HIT_ROT) {
            startBgRotate(capturedPos);
            return;
          }

          // Resize handles — transform click into image-local space then hit-test
          const HIT = 12;
          const dx = stageX - imageCx;
          const dy = stageY - imageCy;
          const localX = dx * Math.cos(-rot) - dy * Math.sin(-rot) + bt.w / 2;
          const localY = dx * Math.sin(-rot) + dy * Math.cos(-rot) + bt.h / 2;
          const bgHandles = [
            { id: 'nw', x: 0,         y: 0         }, { id: 'ne', x: bt.w,      y: 0         },
            { id: 'se', x: bt.w,      y: bt.h      }, { id: 'sw', x: 0,          y: bt.h      },
            { id: 'n',  x: bt.w / 2,  y: 0         }, { id: 's',  x: bt.w / 2,  y: bt.h      },
            { id: 'e',  x: bt.w,      y: bt.h / 2  }, { id: 'w',  x: 0,          y: bt.h / 2  },
          ];
          const hitHandle = bgHandles.find(c =>
            Math.abs(localX - c.x) <= HIT && Math.abs(localY - c.y) <= HIT
          );
          if (hitHandle) {
            startBgCornerResize(hitHandle.id, capturedPos);
            return;
          }
        }

        const clickedShape = hitTestShape(stageX, stageY);

        // Check if click lands inside the bg image area (rotation-aware, no shape on top)
        const t = bgTransformRef.current;
        let onBg = false;
        if (!clickedShape && t && !bgLockedRef.current) {
          const trot = t.rot ?? 0;
          const tcx = t.x + t.w / 2;
          const tcy = t.y + t.h / 2;
          const tdx = stageX - tcx;
          const tdy = stageY - tcy;
          const tlx = tdx * Math.cos(-trot) - tdy * Math.sin(-trot) + t.w / 2;
          const tly = tdx * Math.sin(-trot) + tdy * Math.cos(-trot) + t.h / 2;
          onBg = tlx >= 0 && tlx <= t.w && tly >= 0 && tly <= t.h;
        }

        if (onBg) {
          setBgSelected(true);
          bgSelectedRef.current = true;
          bgDragRef.current = { startScreen: capturedPos, originTransform: { ...bgTransformRef.current } };
          setEditorCursor('grabbing');
          const getRect = () => editorRef.current?.getBoundingClientRect();
          const onMove = (ev) => {
            if (!bgDragRef.current) return;
            const r = getRect(); if (!r) return;
            const p = { x: ev.clientX - r.left, y: ev.clientY - r.top };
            const dx = (p.x - bgDragRef.current.startScreen.x) / (zoomRef.current || 1);
            const dy = (p.y - bgDragRef.current.startScreen.y) / (zoomRef.current || 1);
            const o = bgDragRef.current.originTransform;
            const next = { ...o, x: o.x + dx, y: o.y + dy };
            setBgTransform(next);
            bgTransformRef.current = next;
          };
          const onUp = () => {
            if (onBgTransformCommit && bgTransformRef.current) {
              onBgTransformCommit(bgTransformRef.current);
            }
            setEditorCursor('default');
            bgDragRef.current = null;
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
          };
          window.addEventListener('pointermove', onMove);
          window.addEventListener('pointerup', onUp);
          return;
        }

        setBgSelected(false);
        bgSelectedRef.current = false;

        const isDragMove = !!(clickedShape && selectedIds.includes(clickedShape.id));
        leftDownRef.current = { pos: capturedPos, stageX, stageY, clickedShape, isCtrl: capturedIsCtrl, activated: false, isDragMove };
      }, 0);
    }
  }, [isLocked, viewOnly, hitTestShape, panX, panY, zoom, selectedIds, startBgCornerResize, startBgRotate, startShapeAnchorResize, startShapeRotate]);
  
  // Handle mouse move for rectangle selection, dragging, and viewport panning
  const handleMouseMove = useCallback((event) => {
    // PRIORITY: resizing > panning > dragging
    if (isResizing) {
      const pos = getPointerPos(event);
      const resizeOffset = {
        x: (pos.x - resizeStart.x) / zoom,
        y: (pos.y - resizeStart.y) / zoom
      };
      pendingRef.current.resize = resizeOffset;
      if (!moveRafRef.current) {
        moveRafRef.current = requestAnimationFrame(flushMoveFrame);
      }
      return;
    }

    if (isPanning) {
      // Handle viewport panning
      let pos = { x: 0, y: 0 };
      
      try {
        if (event.data && event.data.originalEvent) {
          const rect = event.currentTarget.getBoundingClientRect();
          pos = {
            x: event.data.originalEvent.clientX - rect.left,
            y: event.data.originalEvent.clientY - rect.top
          };
        } else if (event.nativeEvent) {
          const rect = event.currentTarget.getBoundingClientRect();
          pos = {
            x: event.nativeEvent.clientX - rect.left,
            y: event.nativeEvent.clientY - rect.top
          };
        } else if (event.data && event.data.global) {
          pos = event.data.global;
        }
      } catch (error) {
        return;
      }
      
      // Calculate pan offset
      const deltaX = pos.x - panStartPos.x;
      const deltaY = pos.y - panStartPos.y;
      
      const newPanX = panStartOffset.x + deltaX;
      const newPanY = panStartOffset.y + deltaY;
      
      // rAF throttle
      pendingRef.current.pan = { x: newPanX, y: newPanY };
      if (!moveRafRef.current) {
        moveRafRef.current = requestAnimationFrame(flushMoveFrame);
      }
      return;
    }
    
    // Check left-drag threshold — activate selection rectangle once moved far enough
    if (leftDownRef.current && !leftDownRef.current.activated) {
      let dpos = { x: 0, y: 0 };
      try {
        if (event.data && event.data.originalEvent) {
          const rect = event.currentTarget.getBoundingClientRect();
          dpos = {
            x: event.data.originalEvent.clientX - rect.left,
            y: event.data.originalEvent.clientY - rect.top
          };
        } else if (event.nativeEvent) {
          const rect = event.currentTarget.getBoundingClientRect();
          dpos = {
            x: event.nativeEvent.clientX - rect.left,
            y: event.nativeEvent.clientY - rect.top
          };
        }
      } catch (e) {}
      const dx = dpos.x - leftDownRef.current.pos.x;
      const dy = dpos.y - leftDownRef.current.pos.y;
      if (Math.sqrt(dx * dx + dy * dy) > 5) {
        if (viewOnly) {
          // viewOnly: left-drag on empty space starts a marquee to mark multiple
          // chairs (e.g. for bulk reset). No panning (right-drag pans) and shapes
          // aren't movable here, so always start a selection rectangle.
          leftDownRef.current.activated = true;
          setIsSelecting(true);
          selectionAnchorRef.current = { x: leftDownRef.current.stageX, y: leftDownRef.current.stageY };
          setCurrentSelectionRect({
            x: leftDownRef.current.stageX,
            y: leftDownRef.current.stageY,
            width: 0,
            height: 0
          });
        } else {
          leftDownRef.current.activated = true;
          if (leftDownRef.current.isDragMove) {
            // Dragging selected shapes — start move
            setIsDragging(true);
            setDragStart(leftDownRef.current.pos);
            setDragOffset({ x: 0, y: 0 });
            const positions = new Map();
            selectedIds.forEach(id => {
              const s = shapeMap.get(id);
              if (s) positions.set(id, { x: s.x, y: s.y });
            });
            setOriginalPositions(positions);
          } else {
            // Dragging empty space — start selection rectangle
            setIsSelecting(true);
            selectionAnchorRef.current = { x: leftDownRef.current.stageX, y: leftDownRef.current.stageY };
            setCurrentSelectionRect({
              x: leftDownRef.current.stageX,
              y: leftDownRef.current.stageY,
              width: 0,
              height: 0
            });
          }
        }
      }
      return;
    }

    if (isDragging) {
      // Move selected shapes
      let pos = { x: 0, y: 0 };
      try {
        if (event.data && event.data.originalEvent) {
          const rect = event.currentTarget.getBoundingClientRect();
          pos = { x: event.data.originalEvent.clientX - rect.left, y: event.data.originalEvent.clientY - rect.top };
        } else if (event.nativeEvent) {
          const rect = event.currentTarget.getBoundingClientRect();
          pos = { x: event.nativeEvent.clientX - rect.left, y: event.nativeEvent.clientY - rect.top };
        } else if (event.data && event.data.global) {
          pos = event.data.global;
        }
      } catch (e) { return; }
      const totalOffset = {
        x: (pos.x - dragStart.x) / zoom,
        y: (pos.y - dragStart.y) / zoom
      };
      pendingRef.current.drag = totalOffset;
      if (!moveRafRef.current) {
        moveRafRef.current = requestAnimationFrame(flushMoveFrame);
      }
      return;
    }

    if (!isSelecting) return;
    
    // Safely get position with proper coordinate calculation
    let pos = { x: 0, y: 0 };
    
    try {
      if (event.data && event.data.getLocalPosition) {
        pos = event.data.getLocalPosition(event.currentTarget);
      } else if (event.data && event.data.originalEvent) {
        // Fallback to manual calculation
        const rect = event.currentTarget.getBoundingClientRect();
        pos = {
          x: event.data.originalEvent.clientX - rect.left,
          y: event.data.originalEvent.clientY - rect.top
        };
      } else if (event.nativeEvent) {
        // Manual coordinate calculation
        const rect = event.currentTarget.getBoundingClientRect();
        pos = {
          x: event.nativeEvent.clientX - rect.left,
          y: event.nativeEvent.clientY - rect.top
        };
      }
    } catch (error) {
      // Fallback position
      pos = { x: 0, y: 0 };
    }
    
    // Transform screen coordinates to stage coordinates
    const stageX = (pos.x - panX) / zoom;
    const stageY = (pos.y - panY) / zoom;
    const anchor = selectionAnchorRef.current;
    
    const newRect = {
      x: Math.min(anchor.x, stageX),
      y: Math.min(anchor.y, stageY),
      width: Math.abs(stageX - anchor.x),
      height: Math.abs(stageY - anchor.y)
    };
    
    // rAF throttle
    pendingRef.current.selection = newRect;
    if (!moveRafRef.current) {
      moveRafRef.current = requestAnimationFrame(flushMoveFrame);
    }
    
    // Don't calculate intersecting shapes during drag - only show rectangle
    // Shape calculation will happen on mouse up for better performance
  }, [isSelecting, isDragging, dragStart, isPanning, panStartPos, panStartOffset, zoom, panX, panY, selectedIds, shapeMap, flushMoveFrame, viewOnly]);
  
  // Handle mouse up for rectangle selection and viewport panning
  const handleMouseUp = useCallback((event) => {
    // Determine which button was released
    let isRightUp = false;
    try {
      if (event.data && event.data.originalEvent) {
        isRightUp = event.data.originalEvent.button === 2;
      } else if (event.nativeEvent) {
        isRightUp = event.nativeEvent.button === 2;
      }
    } catch (e) {}

    if (isRightUp || isPanning) {
      setIsPanning(false);
      return;
    }

    if (isDragging) {
      // A move still waiting for its rAF flush would otherwise re-set dragOffset after the reset below.
      const finalOffset = pendingRef.current.drag ?? dragOffset;
      pendingRef.current.drag = null;
      const positionUpdates = [];
      selectedIds.forEach(id => {
        const originalPos = originalPositions.get(id);
        if (originalPos) {
          positionUpdates.push({
            id,
            x: originalPos.x + (finalOffset?.x ?? 0),
            y: originalPos.y + (finalOffset?.y ?? 0)
          });
        }
      });
      if (onUpdateShapePositions && positionUpdates.length > 0) {
        onUpdateShapePositions(positionUpdates);
        if (onCommitShapes) onCommitShapes(positionUpdates);
      }
      setIsDragging(false);
      setDragStart({ x: 0, y: 0 });
      setDragOffset(null);
      setOriginalPositions(new Map());
      leftDownRef.current = null;
      return;
    }

    if (isResizing) {
      // Complete the resize operation
      // Apply final resize transformation to shapes
      applyResizeTransformation(resizeOffset, resizeAnchor, true);
      
      // Reset resize state
      setIsResizing(false);
      isResizingRef.current = false;
      setResizeAnchor(null);
      setOriginalBounds({});
      setResizeOffset({ x: 0, y: 0 });
      // Detach global listeners if present
      if (globalResizeHandlersRef.current.move) {
        window.removeEventListener('pointermove', globalResizeHandlersRef.current.move);
      }
      if (globalResizeHandlersRef.current.up) {
        window.removeEventListener('pointerup', globalResizeHandlersRef.current.up);
      }
      globalResizeHandlersRef.current = { move: null, up: null };
      return;
    }
    
    if (isSelecting) {
      // Finalise rectangle selection
      setIsSelecting(false);
      leftDownRef.current = null;

      if (currentSelectionRect && currentSelectionRect.width > 5 && currentSelectionRect.height > 5) {
      // Calculate intersecting shapes on mouse up for better performance
      const intersectingShapes = shapes.filter(shape => {
        const shapeRight = shape.x + shape.width;
        const shapeBottom = shape.y + shape.height;
        const selectionRight = currentSelectionRect.x + currentSelectionRect.width;
        const selectionBottom = currentSelectionRect.y + currentSelectionRect.height;
        
        const intersects = !(shape.x >= selectionRight || 
                           shapeRight <= currentSelectionRect.x || 
                           shape.y >= selectionBottom || 
                           shapeBottom <= currentSelectionRect.y);
        
        return intersects;
      });
      
      const selectedShapeIds = intersectingShapes.map(shape => shape.id);
      
      if (selectedShapeIds.length > 0) {
        if (onMultiSelect) {
          onMultiSelect(selectedShapeIds);
          // Set flag to prevent immediate deselection
          setJustCompletedSelection(true);
          
          // Reset the flag after a short delay
          setTimeout(() => {
            setJustCompletedSelection(false);
          }, 100);
        }
      } else {
        // If no shapes found, deselect all
        onSelect?.(null);
      }
      } else {
        // Rectangle too small — treat as plain click
        onSelect?.(null);
      }

      setCurrentSelectionRect(null);
      setPreviewSelectedIds([]);
      return;
    }

    // Plain left click: select/deselect on mouse-up (no drag activated)
    if (leftDownRef.current) {
      const { clickedShape, isCtrl } = leftDownRef.current;
      leftDownRef.current = null;
      if (clickedShape) {
        if (isCtrl) {
          onSelect(clickedShape.id, true);
        } else {
          onSelect(clickedShape.id);
        }
      } else {
        onSelect?.(null);
      }
    }
  }, [isSelecting, currentSelectionRect, onMultiSelect, onSelect, shapes, isPanning, isDragging, dragOffset, selectedIds, originalPositions, onUpdateShapePositions, isResizing, applyResizeTransformation, resizeOffset, resizeAnchor]);
  
  
  return (
    <div ref={editorRef} style={{ width: '100%', height: '100%', position: 'relative' }} onContextMenu={(e) => e.preventDefault()}>
      {debugMode && (
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          background: 'rgba(0, 0, 0, 0.8)',
          color: 'white',
          padding: '10px',
          borderRadius: '6px',
          fontSize: '12px',
          zIndex: 1000,
          minWidth: '200px',
          fontFamily: 'monospace'
        }}>
          <div style={{ fontWeight: 'bold', color: '#2196F3', marginBottom: 4 }}>Selection Status</div>
          <div style={{ marginBottom: 8, borderBottom: '1px solid #333', paddingBottom: 4 }}>
            Selected: {selectedIds.length} shapes
          </div>
          <div style={{ fontWeight: 'bold', color: '#FF5722' }}>Performance Monitor</div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>FPS:</span>
            <span style={{ color: fps >= 55 ? '#00ff00' : fps >= 30 ? '#ffaa00' : '#ff4444' }}>{fps}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Memory:</span>
            <span>{memoryUsage} MB</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Shapes:</span>
            <span>{shapes.length.toLocaleString()}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Visible:</span>
            <span>{visibleShapes.length.toLocaleString()}</span>
          </div>
        </div>
      )}
      <Stage
        width={width}
        height={height}
        onMount={(stage) => {
          stageRef.current = stage;
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={(event) => {
           try {
             if (event.data && event.data.getLocalPosition) {
               const pos = event.data.getLocalPosition(event.currentTarget);
               pendingRef.current.mouse = pos;
               // JS hover hit-test in stage coordinates
               const sx = (pos.x - panX) / zoom;
               const sy = (pos.y - panY) / zoom;
               const hit = hitTestShape(sx, sy);
               const newHoverId = hit ? hit.id : null;
               if (newHoverId !== hoveredId) setHoveredId(newHoverId);
               if (!moveRafRef.current) {
                 moveRafRef.current = requestAnimationFrame(flushMoveFrame);
               }
             }
           } catch (error) {}
           handleMouseMove(event);
         }}
        onMouseUp={handleMouseUp}
        options={{
          backgroundColor: 0xf0f0f0,
          antialias: true,
          resolution: window.devicePixelRatio || 1,
          autoDensity: true,
          preserveDrawingBuffer: true
        }}
        style={{
          display: 'block',
          width: '100%',
          height: '100%'
        }}
      >
        {/* Main content container with zoom and pan */}
        <Container
          scale={zoom}
          x={panX}
          y={panY}
          eventMode="static"
        >
          <BackgroundImageComponent 
            backgroundImage={backgroundImage}
            bgTransform={bgTransform}
          />
          {/* Single batched draw call for all visible shapes */}
          <BatchedShapesLayer
            shapes={visibleShapes}
            selectedIds={selectedIds}
            firstMarkers={firstMarkers}
            selectedMarker={selectedMarker}
            guestList={guestList}
            viewOnly={viewOnly}
            dragOffset={dragOffset}
            hoveredId={hoveredId}
          />
          {/* Text labels only for shapes that have them */}
          <ShapeLabels
            shapes={visibleShapes}
            selectedIds={selectedIds}
            dragOffset={dragOffset}
          />
          {selectedBounds && selectedIds.length > 0 && (
            <BoundingBox
              bounds={selectedBounds}
              onResizeStart={handleResizeStart}
              isResizing={isResizing}
              resizeAnchor={resizeAnchor}
              resizeOffset={resizeOffset}
            />
          )}
          <ScanPathLines 
            scanPaths={scanPaths}
            shapes={shapes}
            currentScanPathId={currentScanPathId}
          />
          <SelectionRectangle 
            selectionRect={currentSelectionRect || selectionRect}
            visible={isSelecting || showSelectionRect}
          />
          <BgImageOverlay
            bgTransform={bgTransform}
            bgSelected={bgSelected}
          />
        </Container>
      </Stage>

      {/* Zoom controls */}
      <div style={{
        position: 'fixed',
        bottom: 36,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        zIndex: 1000,
        background: 'rgba(0,0,0,0.65)',
        borderRadius: 20,
        padding: '4px 10px',
        userSelect: 'none',
      }}>
        <button
          onClick={() => fitToContent(shapes, width, height)}
          title="Reset zoom"
          style={{
            background: 'rgba(255,255,255,0.15)',
            color: 'white',
            border: 'none',
            borderRadius: 12,
            padding: '3px 10px',
            cursor: 'pointer',
            fontSize: 12,
            fontFamily: 'monospace',
          }}
        >
          Reset
        </button>
        <span style={{ color: 'white', fontFamily: 'monospace', fontSize: 12, minWidth: 50, textAlign: 'center' }}>
          {zoom > 1 ? `+${((zoom - 1) * 100).toFixed(0)}%` : zoom < 1 ? `-${((1 - zoom) * 100).toFixed(0)}%` : '100%'}
        </span>
      </div>

      {/* Bend overlay – SVG handles rendered over the Pixi canvas */}
      {bendMode && selectedIds.length > 0 && (
        <>
          <BendLayer
            orientation="vertical"
            selectedIds={selectedIds}
            shapes={shapes}
            zoom={zoom}
            panX={panX}
            panY={panY}
            onApplyBend={onApplyBend}
          />
          <BendLayer
            orientation="horizontal"
            selectedIds={selectedIds}
            shapes={shapes}
            zoom={zoom}
            panX={panX}
            panY={panY}
            onApplyBend={onApplyBend}
          />
        </>
      )}
    </div>
  );
});

export default PixiEditorSimple;

